import React, { createContext, useContext, useState, useCallback } from 'react';
import debug from '@commons-shared/debug';
import { config } from '../config/environment';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [loadingType, setLoadingType] = useState('info'); // Nouveau: type de l'overlay
  
  // États pour MessageLog
  const [messageLogType, setMessageLogType] = useState('');
  const [messageLogText, setMessageLogText] = useState('');
  const [messageLogKey, setMessageLogKey] = useState(0);

  // Fonction pour démarrer le loading
  const startLoading = useCallback((message = 'Chargement en cours...', type = 'info') => {
    debug.log(config, `LoadingContext startLoading appelée: ${type} - ${message}`);
    setIsLoading(true);
    setLoadingMessage(message);
    setLoadingType(type);
    
    // Logger le message selon le type
    switch (type) {
      case 'error':
        debug.error(config, message);
        break;
      case 'warning':
        debug.warn(config, message);
        break;
      case 'success':
        debug.success(config, message);
        break;
      case 'info':
      default:
        debug.log(config, message);
        break;
    }
  }, []);

  // Fonction pour afficher un message via MessageLog
  const showMessage = useCallback((type, text, autoHide = false, duration = 5000) => {
    debug.log(config, `LoadingContext showMessage appelée: ${type} - ${text} (autoHide: ${autoHide}, duration: ${duration})`);
    setMessageLogType(type);
    setMessageLogText(text);
    setMessageLogKey(prev => prev + 1); // Force la re-création du MessageLog
    
    // Si autoHide est false, garder le message visible
    if (!autoHide) {
      return;
    }
    
    // Si autoHide est true, vider le message après la durée
    setTimeout(() => {
      debug.log(config, `LoadingContext showMessage timeout expiré pour: ${text}`);
      setMessageLogText('');
      setMessageLogType('');
    }, duration);
  }, []);

  // Fonction pour arrêter le loading
  const stopLoading = useCallback(() => {
    debug.log(config, 'LoadingContext stopLoading appelée');
    setIsLoading(false);
    setLoadingMessage('');
    // Ne pas afficher de message automatique de fin - laisser l'action gérer ses propres messages
    // showMessage('success', 'Opération terminée', true, 3000);
  }, []);

  // Fonction pour mettre à jour le message de loading
  const updateLoadingMessage = useCallback((message, type = null) => {
    debug.log(config, `LoadingContext updateLoadingMessage appelée: ${type || loadingType} - ${message}`);
    setLoadingMessage(message);
    if (type) {
      setLoadingType(type);
    }
    
    // Logger le message selon le type
    const currentType = type || loadingType;
    switch (currentType) {
      case 'error':
        debug.error(config, message);
        break;
      case 'warning':
        debug.warn(config, message);
        break;
      case 'success':
        debug.success(config, message);
        break;
      case 'info':
      default:
        debug.log(config, message);
        break;
    }
  }, [loadingType]);

  // Fonction pour vider les messages
  const clearMessage = useCallback(() => {
    setMessageLogText('');
    setMessageLogType('');
  }, []);

  // Fonction pour afficher une erreur
  const showError = useCallback((message, autoHide = false, duration = 8000) => {
    showMessage('error', message, autoHide, duration);
  }, [showMessage]);

  // Fonction pour afficher un succès
  const showSuccess = useCallback((message, autoHide = true, duration = 3000) => {
    debug.log(config, `LoadingContext showSuccess appelée: ${message}`);
    showMessage('success', message, autoHide, duration);
  }, [showMessage]);

  // Fonction pour afficher un warning
  const showWarning = useCallback((message, autoHide = false, duration = 6000) => {
    showMessage('warning', message, autoHide, duration);
  }, [showMessage]);

  const value = {
    isLoading,
    loadingMessage,
    loadingType,
    messageLogType,
    messageLogText,
    messageLogKey,
    startLoading,
    stopLoading,
    showMessage,
    updateLoadingMessage,
    clearMessage,
    showError,
    showSuccess,
    showWarning
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
};
